import { type ReactNode, useState } from 'react';

import { Link as LinkIcon, Loader2, Scroll } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { downloadFile } from '@/utils/downloadFile';
import type { WorkflowCardDocumentation } from './workspaceFilterMatch';

type Props = {
  documentation: WorkflowCardDocumentation;
  rowId: string;
};

// Ghost (icon + "Doc" text) at rest; on hover/focus it becomes exactly like the
// adjacent "Open" button: rounded border + card background, no shadow. A
// transparent border is always reserved so the box size does not shift on
// hover. `relative z-10` keeps the control clickable above the card's full-card
// link overlay.
const DOC_BUTTON_CLASS =
  'relative z-10 inline-flex items-center gap-1 rounded-md border border-transparent bg-transparent px-3 py-1.5 font-medium text-foreground text-xs no-underline outline-none transition-colors hover:border-border hover:bg-card focus-visible:border-border focus-visible:bg-card disabled:pointer-events-none disabled:opacity-50';

export const WorkflowDocButton = ({ documentation, rowId }: Props) => {
  const [loading, setLoading] = useState(false);
  const { file, url } = documentation;

  const withTooltip = (trigger: ReactNode, label: string) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent variant="contrast">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (file) {
    return withTooltip(
      <button
        aria-label="Download documentation"
        className={DOC_BUTTON_CLASS}
        data-testid={`workflow-card-doc-${rowId}`}
        disabled={loading}
        onClick={() => downloadFile(file, setLoading, loading)}
        type="button"
      >
        {loading ? (
          <Loader2 className="shrink-0 animate-spin" size={14} />
        ) : (
          <Scroll className="shrink-0" size={14} />
        )}
        Doc
      </button>,
      'Download documentation'
    );
  }

  if (url) {
    return withTooltip(
      <a
        aria-label="Open documentation in a new tab"
        className={DOC_BUTTON_CLASS}
        data-testid={`workflow-card-doc-${rowId}`}
        href={url}
        rel="noopener noreferrer"
        target="_blank"
      >
        <LinkIcon className="shrink-0" size={14} />
        Doc
      </a>,
      'Open documentation in a new tab'
    );
  }

  return null;
};
